import ServiceFillIcon from '@ben/icons/service-fill';
import { TagColorVariant } from '@ben/orion/constants/display-status';
import type { TableColumn } from '@ben/orion/table';
import Table, { LinkCell } from '@ben/orion/table';
import Tag, { TagVariant } from '@ben/orion/tag';
import type { IModularBenefitsListItem } from '@ben/shared/types/columbia';
import { decodeHtmlEntities } from '@ben/shared/utils/decode-html-entities';
import type { ColumnSort } from '@tanstack/react-table';
import { memo } from 'react';
import { useNavigate } from 'react-router-dom';

interface BenefitsTableProps {
  benefits?: IModularBenefitsListItem[];
  pageIndex: number;
  pageSize: number;
  onChangePage: (pageIndex: number) => void;
  onChangePageSize: (pageSize: number) => void;
  onChangeSort: (sortState: ColumnSort) => void;
  rowCount?: number;
  searchQuery?: string;
  isLoading: boolean;
}

const BenefitsTable = ({
  benefits,
  pageIndex,
  pageSize,
  onChangePage,
  onChangePageSize,
  onChangeSort,
  rowCount,
  searchQuery,
  isLoading,
}: BenefitsTableProps) => {
  const navigate = useNavigate();

  const getBenefitLink = (id: string) => {
    const query = searchQuery ? `?search=${searchQuery}` : '';
    return `/benefits/${id}/${query}`;
  };

  const columns: TableColumn<IModularBenefitsListItem>[] = [
    {
      id: 'id',
      header: 'ID',
      accessorKey: 'id',
      meta: { hug: true },
      sortDescFirst: false, // Because benefits are already sorted by id desc
    },
    {
      id: 'name',
      header: 'Name',
      accessorFn: (data) => decodeHtmlEntities(data.name),
      cell: ({ cell, row: { original: data } }) => (
        <LinkCell to={getBenefitLink(data.id)}>
          {cell.getValue<string>()}
        </LinkCell>
      ),
      meta: { truncate: true, flex: 1.5 },
    },
    {
      header: 'Employer Name',
      accessorKey: 'employer',
      enableSorting: false,
    },
    {
      id: 'active',
      header: 'Status',
      accessorKey: 'active',
      cell: ({ row: { original: data } }) => {
        const colorVariant = data.active
          ? TagColorVariant.Success
          : TagColorVariant.Neutral;
        return (
          <Tag
            variant={TagVariant.Outlined}
            name={data.active ? 'Active' : 'Inactive'}
            colorVariant={colorVariant}
          />
        );
      },
    },
  ];

  return (
    <Table<IModularBenefitsListItem>
      columns={columns}
      data={benefits}
      ariaLabel="Modular Benefits"
      isLoading={isLoading}
      onRowClick={({ original: benefit }) => {
        navigate(getBenefitLink(benefit.id));
      }}
      actions={[
        {
          label: 'Clone',
          onClick: (benefit) => navigate(`/benefits/clone/${benefit.id}`),
        },
      ]}
      manualPagination={{ pageIndex, pageSize }}
      onChangePage={onChangePage}
      onChangePageSize={onChangePageSize}
      onChangeSort={onChangeSort}
      rowCount={rowCount}
      manualSorting
      emptyState={{
        icon: ServiceFillIcon,
        title: 'No benefits found',
        info: 'Try adjusting your search term.',
      }}
    />
  );
};

export default memo(BenefitsTable);
